/* eslint-disable react/prop-types */
import React from 'react';
import { useTheme } from 'styled-components';
import HelpTip from '../HelpTip';
import SkeletonBar from '../Skeleton';
import { tokens } from '../tokens';

// Quantities-only breakdown table for report screens (gen-3). A card with an
// orange-bar title, an optional right-aligned stat, and a bordered two-column
// table. Rows can be grouped under uppercase section headers (e.g. event
// days), and a row can carry tier lines that render in a recessed inset panel
// beneath it.
//
// groups: [{ label, total, rows: [{ name, qty, tiers: [{ name, qty }] }] }]
// A group without a `label` renders its rows with no section header.

const SectionHeader = ({ label, total, unitLabel }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 20px',
        background: t.inset,
        borderBottom: '1px solid ' + t.line,
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
          color: t.primary,
        }}
      >
        {label}
      </span>
      {null == total ? null : (
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 14.5,
            fontWeight: 600,
            color: t.sub,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span style={{ color: t.text, fontWeight: 800 }}>{total}</span>{' '}
          {unitLabel}
        </span>
      )}
    </div>
  );
};

const Qty = ({ children, strong, small }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        fontSize: small ? 15 : 16,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
        fontWeight: strong ? 800 : 400,
        color: strong ? t.text : t.sub,
      }}
    >
      {children}
    </div>
  );
};

const TicketRow = ({ row, last, showPrice }) => {
  const t = tokens(useTheme());
  const hasTiers = row.tiers && row.tiers.length > 0;
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showPrice ? '1fr 130px 90px' : '1fr 90px',
          alignItems: 'center',
          padding: '12px 20px',
          borderBottom: hasTiers || last ? 'none' : '1px solid ' + t.line,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: t.text,
            paddingRight: 12,
            overflowWrap: 'anywhere',
          }}
        >
          {row.name}
        </div>
        {showPrice ? <Qty>{row.price || ''}</Qty> : null}
        <Qty strong>{row.qty}</Qty>
      </div>
      {hasTiers ? (
        <div
          style={{
            padding: '0 14px 12px',
            borderBottom: last ? 'none' : '1px solid ' + t.line,
          }}
        >
          <div
            style={{
              background: t.inset,
              border: '1px solid ' + t.line,
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            {row.tiers.map((tier, i) => (
              <div
                key={tier.name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: showPrice
                    ? '1fr 130px 90px'
                    : '1fr 90px',
                  alignItems: 'center',
                  padding: '8px 14px',
                  borderTop: 0 === i ? 'none' : '1px solid ' + t.line,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: t.sub,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {tier.name}
                </div>
                {showPrice ? <Qty small>{tier.price || ''}</Qty> : null}
                <Qty small>{tier.qty}</Qty>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
};

// Shimmer rows while the report loads: a section-header band, then ticket
// rows with a name bar and a short qty bar. Widths vary so the block reads
// as content, not stripes.
const SKELETON_WIDTHS = ['62%', '48%', '55%', '40%', '58%'];

const SkeletonRows = () => {
  const t = tokens(useTheme());
  return (
    <>
      <div
        style={{
          padding: '13px 20px',
          background: t.inset,
          borderBottom: '1px solid ' + t.line,
        }}
      >
        <SkeletonBar $w="120px" $h="12px" />
      </div>
      {SKELETON_WIDTHS.map((width, i) => (
        <div
          key={width}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 90px',
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom:
              i === SKELETON_WIDTHS.length - 1 ? 'none' : '1px solid ' + t.line,
          }}
        >
          <SkeletonBar $w={width} $h="15px" />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <SkeletonBar $w="26px" $h="15px" />
          </div>
        </div>
      ))}
    </>
  );
};

const BreakdownTable = ({
  title,
  help,
  stat,
  columnLabel = 'Ticket',
  unitLabel = 'tickets',
  showPrice = false,
  groups,
  footer,
  loading,
  emptyLabel = 'No tickets sold.',
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const rowCount = (groups || []).reduce(
    (acc, group) => acc + (group.rows || []).length,
    0
  );

  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        padding: '22px 22px 18px',
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 14,
          marginBottom: 18,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            marginRight: 'auto',
          }}
        >
          <span
            style={{
              width: 4,
              height: 22,
              borderRadius: 3,
              background: t.primary,
            }}
          />
          <span style={{ fontSize: 19, fontWeight: 800, color: t.text }}>
            {title}
          </span>
          {help ? <HelpTip content={help} /> : null}
        </div>
        {stat ? (
          <div style={{ textAlign: 'right' }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.7px',
                textTransform: 'uppercase',
                color: t.label,
              }}
            >
              {stat.label}
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: '-0.5px',
                lineHeight: 1.2,
                color: t.text,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {stat.value}
            </div>
          </div>
        ) : null}
      </div>

      <div
        style={{
          border: '1px solid ' + t.border,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: showPrice ? '1fr 130px 90px' : '1fr 90px',
            alignItems: 'center',
            padding: '12px 20px',
            background: t.head,
            borderBottom: '1px solid ' + t.border,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: t.label,
            }}
          >
            {columnLabel}
          </div>
          {showPrice ? (
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                color: t.label,
                textAlign: 'right',
              }}
            >
              Price
            </div>
          ) : null}
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: t.label,
              textAlign: 'right',
            }}
          >
            Qty
          </div>
        </div>

        {loading ? (
          <SkeletonRows />
        ) : 0 === rowCount ? (
          <div
            style={{
              padding: '30px 20px',
              textAlign: 'center',
              fontSize: 14,
              color: t.sub,
            }}
          >
            {emptyLabel}
          </div>
        ) : (
          (groups || []).map((group, groupIndex) => {
            const lastGroup = groupIndex === groups.length - 1;
            return (
              <React.Fragment key={group.label || groupIndex}>
                {group.label ? (
                  <SectionHeader
                    label={group.label}
                    total={group.total}
                    unitLabel={unitLabel}
                  />
                ) : null}
                {(group.rows || []).map((row, rowIndex) => (
                  <TicketRow
                    key={row.name}
                    row={row}
                    showPrice={showPrice}
                    last={
                      lastGroup && !footer && rowIndex === group.rows.length - 1
                    }
                  />
                ))}
              </React.Fragment>
            );
          })
        )}

        {footer && !loading && rowCount > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: showPrice ? '1fr 130px 90px' : '1fr 90px',
              alignItems: 'center',
              padding: '13px 20px',
              background: t.head,
              borderTop: '1px solid ' + t.border,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
                color: t.label,
              }}
            >
              {footer.label}
            </div>
            {showPrice ? <div /> : null}
            <Qty strong>{footer.value}</Qty>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BreakdownTable;
